import {
  OmniElement,
  OmniStyleElement,
  createRef,
  choose,
  ref,
  html,
  css,
} from 'omni-ui';

import * as bpConstants from '../../services/blueprint-graph/constants.js';
import * as textService from '../../services/blueprint-graph/tiptap-wysiwyg.js';

import '../campaign-wysiwyg.js';
import '../campaign-color-picker.js';

OmniStyleElement.register();

/**
 * @property {import('../../services/blueprint-graph.js').} graphEditor
 * @property {bpConstants.cellTypes} vertexType
 * @property {Object} computedOmniStyle
 */
export class CampaignBlueprintVertexEditor extends OmniElement {
  static properties = {
    graphEditor: { type: Object, state: true },
    vertexType: { type: Symbol, state: true },
    computedOmniStyle: { type: Object, state: true },
  };

  static styles = [
    ...super.styles,
    css`
      .edit-popup-body {
        padding: 1.714rem;
      }
    `,
  ];

  constructor() {
    super();
    this.omniStyleRef = createRef();
    this.vertexType = null;
  }

  firstUpdated() {
    this.computedOmniStyle = getComputedStyle(this.omniStyleRef.value);
  }

  #dispatchVertexChange = () => {
    const [vertex] = this.graphEditor.selection;
    this.dispatchNewEvent('vertex-change', vertex);
  };

  get #textFieldsTemplate() {
    const { computedOmniStyle, graphEditor } = this;
    const [initialActiveElement] = graphEditor.selection;
    const handleColorChange = ({ target: { value, checked } }) => {
      const [activeElement] = graphEditor.selection;
      if (checked) {
        activeElement.color = value;
      }
      this.#dispatchVertexChange();
    };

    /** @todo: Should these just be properties on the BlueprintText instance? */
    // There's a delay for the ref so the first render, computedOmniStyle will be unavailable
    const colorDark = computedOmniStyle?.getPropertyValue('--color-core-dark');
    const colorLight =
      computedOmniStyle?.getPropertyValue('--color-core-light');

    return html`
      <label class="label ml-0 has-text-weight-bold">Color</label>
      <div>
        <label class="subtitle mb-1">Text Color</label>
        <div>
          <label for="bp-text-color-light" class="mr-2">
            <input
              type="radio"
              name="bp-text-color"
              id="bp-text-color-light"
              .value=${colorLight}
              ?checked=${initialActiveElement.color === colorLight}
              @change=${handleColorChange}
            />
            Light
          </label>
          <label for="bp-text-color-dark">
            <input
              type="radio"
              name="bp-text-color"
              id="bp-text-color-dark"
              .value=${colorDark}
              ?checked=${initialActiveElement.color === colorDark}
              @change=${handleColorChange}
            />
            Dark
          </label>
        </div>
      </div>
      <hr />
      <label class="label ml-0 has-text-weight-bold">Text</label>
      <div>
        <label class="subtitle mb-1">Formatting options</label>
        <campaign-wysiwyg
          @change=${({ detail }) => {
            const [activeElement] = graphEditor.selection;
            const { value } = detail;

            activeElement.updateFromTiptap(value);
            this.#dispatchVertexChange();
          }}
          .initialContent=${textService.toTiptap(
            initialActiveElement.text,
            initialActiveElement.styles,
          )}
          .enabledExtensions=${['bold', 'italic', 'underline']}
        ></campaign-wysiwyg>
      </div>
    `;
  }

  get #shapeFieldsTemplate() {
    const { computedOmniStyle, graphEditor } = this;
    const [initialActiveElement] = graphEditor.selection;

    // TEXT TEST
    /** @todo: Should these just be properties on the BlueprintText instance? */
    // There's a delay for the ref so the first render, computedOmniStyle will be unavailable
    const colorDark = computedOmniStyle?.getPropertyValue('--color-core-dark');
    const colorLight =
      computedOmniStyle?.getPropertyValue('--color-core-light');

    const handlers = {
      color: ({ detail: { color } }) => {
        const [activeElement] = graphEditor.selection;

        /** @todo: Determine if rendering every time heavily impacts performance, or if it's queued or there's a better approach */
        activeElement.fill = color;
        graphEditor.render(false);
        this.#dispatchVertexChange();
      },
      shape: e => {
        const {
          target: { value },
        } = e;
        const selectedShapeType = value.toLowerCase();
        const [currentShape] = graphEditor.selection;

        graphEditor.replaceShape(currentShape, selectedShapeType);
        this.#dispatchVertexChange();
      },
      outline: e => {
        const { target } = e;
        const [activeElement] = graphEditor.selection;
        const strokeColor =
          computedOmniStyle.getPropertyValue('--color-core-dark');

        /** @todo: need to set initial value based on stroke/strokeWidth */
        const lineStyle = target.value;

        /** @todo: move all this to getters/setters on blueprint shape */
        switch (lineStyle) {
          case 'single':
            activeElement.ref.set('stroke', strokeColor);
            activeElement.ref.set('strokeDashArray', null);
            break;
          case 'dashed':
            activeElement.ref.set('stroke', strokeColor);
            activeElement.ref.set('strokeDashArray', [10]);
            break;
          case 'dotted':
            activeElement.ref.set('stroke', strokeColor);
            activeElement.ref.set('strokeDashArray', [3]);
            break;
          /** @todo: figure out what to do here? */
          // case 'double':
          default:
            activeElement.ref.set('stroke', 'rgba(0,0,0,0)');
            activeElement.ref.set('strokeDashArray', null);
            break;
        }

        // Make sure it's caught in the next render
        activeElement.ref.set('dirty', true);

        graphEditor.render();
        this.#dispatchVertexChange();
      },
      textColor: ({ target: { value, checked } }) => {
        const [activeElement] = graphEditor.selection;
        if (checked) {
          activeElement.textColor = value;
          activeElement.defaultTextColor = value;
        }

        this.#dispatchVertexChange();
      },
      textAlign: ({ target: { value, checked } }) => {
        const [activeElement] = graphEditor.selection;
        if (checked) {
          activeElement.textAlign = value;
          activeElement.defaultTextAlign = value;
        }
      },
    };

    const strokeTypes = ['None', 'Single', 'Dashed', 'Dotted'];
    let currentStroke;

    if (initialActiveElement.ref.stroke?.trim()) {
      switch (initialActiveElement.ref.strokeDashArray?.[0]) {
        case undefined: // strokeDashArray will be null
          currentStroke = strokeTypes[1].toLowerCase();
          break;
        case 10:
          currentStroke = strokeTypes[2].toLowerCase();
          break;
        case 3:
          currentStroke = strokeTypes[3].toLowerCase();
          break;
        default:
          currentStroke = strokeTypes[0].toLowerCase();
          break;
      }
    }

    return html`
      <label class="label ml-0 has-text-weight-bold">Color</label>
      <div>
        <label class="subtitle mb-1">Background Color</label>
        <campaign-color-picker
          .color=${initialActiveElement.fill}
          @change=${handlers.color}
        ></campaign-color-picker>
      </div>
      <hr />
      <div class="label ml-0 has-text-weight-bold">
        Shape &amp; Outline Type
      </div>
      <div class="columns">
        <div class="column">
          <label class="subtitle mb-1">Shape</label>
          <div class="select is-fullwidth">
            <select @change=${handlers.shape}>
              ${Object.keys(bpConstants.SHAPE_TYPES).map(shape => {
                const [currentShape] = this.graphEditor?.selection || [];

                return html`<option
                  value=${shape}
                  ?selected=${currentShape.shapeType === shape}
                >
                  ${shape[0].toUpperCase()}${shape.slice(1)}
                </option>`;
              })}
            </select>
          </div>
        </div>
        <div class="column">
          <label class="subtitle mb-1">Outline</label>
          <div class="select is-fullwidth">
            <select @change=${handlers.outline}>
              ${
                /** @todo: move this into shapes as symbols */
                strokeTypes.map(outline => {
                  const value = outline.toLowerCase();
                  return html`
                    <option value=${value} ?selected=${value === currentStroke}>
                      ${outline}
                    </option>
                  `;
                })
              }
            </select>
          </div>
        </div>
      </div>
      <hr />
      <label class="label ml-0 has-text-weight-bold">Text</label>
      <div>
        <label class="subtitle mb-1">Text Color</label>
        <div>
          <label for="bp-text-color-light" class="mr-2">
            <input
              type="radio"
              name="bp-text-color"
              id="bp-text-color-light"
              .value=${colorLight}
              ?checked=${initialActiveElement.textColor === colorLight}
              @change=${handlers.textColor}
            />
            Light
          </label>
          <label for="bp-text-color-dark">
            <input
              type="radio"
              name="bp-text-color"
              id="bp-text-color-dark"
              .value=${colorDark}
              ?checked=${initialActiveElement.textColor === colorDark}
              @change=${handlers.textColor}
            />
            Dark
          </label>
        </div>
      </div>
      <div>
        <label class="subtitle mb-1 mt-3">Text Alignment</label>
        <div>
          <label for="bp-text-align-left" class="mr-2">
            <input
              type="radio"
              name="bp-text-align"
              id="bp-text-align-left"
              value="left"
              ?checked=${initialActiveElement.textAlign === 'left'}
              @change=${handlers.textAlign}
            />
            Left
          </label>
          <label for="bp-text-align-center" class="mr-2">
            <input
              type="radio"
              name="bp-text-align"
              id="bp-text-align-center"
              value="center"
              ?checked=${initialActiveElement.textAlign === 'center'}
              @change=${handlers.textAlign}
            />
            Center
          </label>
          <label for="bp-text-align-right" class="mr-2">
            <input
              type="radio"
              name="bp-text-align"
              id="bp-text-align-right"
              value="right"
              ?checked=${initialActiveElement.textAlign === 'right'}
              @change=${handlers.textAlign}
            />
            Right
          </label>
        </div>
      </div>
      <div>
        <label class="subtitle mb-1 mt-3">Formatting options</label>
        <campaign-wysiwyg
          @change=${({ detail }) => {
            const [activeElement] = graphEditor.selection;
            const { value } = detail;

            activeElement.updateFromTiptap(value);
            this.#dispatchVertexChange();
          }}
          .initialContent=${textService.toTiptap(
            initialActiveElement.text,
            initialActiveElement.textStyles,
          )}
          .enabledExtensions=${['bold', 'italic', 'underline']}
        ></campaign-wysiwyg>
      </div>
    `;
  }

  render() {
    return html`
      <omni-style ${ref(this.omniStyleRef)}>
        <div class="edit-popup-body">
          ${choose(this.vertexType, [
            [bpConstants.CELL_TYPES.shape, () => this.#shapeFieldsTemplate],
            [bpConstants.CELL_TYPES.text, () => this.#textFieldsTemplate],
          ])}
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register(
  'campaign-blueprint-vertex-editor',
  CampaignBlueprintVertexEditor,
);
